// Robust event ID extraction
const getValueAtPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((current, segment) => {
    if (current == null) return undefined;
    if (segment === '0') {
      return Array.isArray(current) ? current[0] : undefined;
    }
    return current[segment];
  }, obj);
};

const searchForReference = (value, visited = new Set()) => {
  if (!value || typeof value !== 'object') return null;
  if (visited.has(value)) return null;
  visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const result = searchForReference(item, visited);
      if (result) return result;
    }
    return null;
  }

  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') {
      const normalizedKey = key.toLowerCase();
      if ((normalizedKey.includes('reference') || normalizedKey === 'id' || normalizedKey === 'event') && entry.trim()) {
        return entry.trim();
      }
    } else if (typeof entry === 'object') {
      const result = searchForReference(entry, visited);
      if (result) return result;
    }
  }

  return null;
};

export const extractEventIdFromResponse = (response, isUpdate, existingId) => {
  if (isUpdate) {
    return existingId; // For updates, use the existing ID
  }

  const candidatePaths = [
    'reference',
    'response.reference',
    'response.response.reference',
    'response.importSummaries.0.reference',
    'response.response.importSummaries.0.reference',
    'importSummaries.0.reference',
    'response.imported.0',
    'response.response.imported.0',
    'imported.0',
    'response.updated.0',
    'response.response.updated.0',
  ];

  for (const path of candidatePaths) {
    const value = getValueAtPath(response, path);
    if (value) return value;
  }

  const fallback = searchForReference(response);
  if (fallback) return fallback;

  console.warn('Could not find event ID in response structure:', response);
  return null;
};

// Fetch all events (KRAs, KPIs, Objectives) from the Objectives program stage
export async function fetchKrasAndKpis(objectiveId, engine) {
  const PROGRAM_STAGE_ID = 'VIBtpvkpHFR'; // Objectives program stage
  const PROGRAM_ID = 'yiT00XKrrHP';

  try {
    // Fetch all events linked to this objective
    const response = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: PROGRAM_ID,
          programStage: PROGRAM_STAGE_ID,
          filter: [`plJovUlxfQ0:eq:${objectiveId}`], // Filter by objective link
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 1000
        }
      }
    });

    // Data element mappings for combined structure
    const KRA_MAPPING = {
      'obapz6Gnyla': 'name',
      'DEECWPTj42E': 'description', 
      'Xl4VEFoIFIw': 'weight'
    };

    const KPI_MAPPING = {
      'DBmylDQA9KX': 'name',
      'rQqF75V9t7K': 'description',
      'Ru68Ax2Xo2L': 'target'
    };

    const mapEvent = (event, mapping) => {
      const mapped = { id: event.event };
      event.dataValues?.forEach(dv => {
        const fieldName = mapping[dv.dataElement];
        if (fieldName) {
          mapped[fieldName] = fieldName === 'weight' || fieldName === 'target' 
            ? Number(dv.value) || 0 
            : dv.value || '';
        }
      });
      return mapped;
    };

    const events = response.events?.events || [];
    
    // Categorize events based on data elements present
    const kras = events.filter(event => 
      event.dataValues?.some(dv => dv.dataElement === 'obapz6Gnyla') // Has KRA name
    ).map(event => mapEvent(event, KRA_MAPPING));

    const kpis = events.filter(event => 
      event.dataValues?.some(dv => dv.dataElement === 'DBmylDQA9KX') // Has KPI name
    ).map(event => mapEvent(event, KPI_MAPPING));

    return { kras, kpis };

  } catch (error) {
    console.error('❌ Error in fetchKrasAndKpis:', error);
    return { kras: [], kpis: [] };
  }
}

// Save or update any event in the Objectives program stage
export async function saveKraOrKpi(eventData, engine) {
  const isUpdate = !!eventData.event;
  
  try {
    // Use direct API calls to avoid engine issues
    const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';
    const url = isUpdate 
      ? `${BASE_URL}/api/events/${eventData.event}`
      : `${BASE_URL}/api/events`;
    
    const response = await fetch(url, {
      method: isUpdate ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
          //...getAuthHeader(), 
      },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    const eventId = extractEventIdFromResponse(result, isUpdate, eventData.event);

    return { 
      success: true, 
      eventId,
      response: result 
    };
    
  } catch (error) {
    // Handle 409 Conflict errors (duplicate events)
    if (error.message?.includes('409') || error.message?.includes('Conflict')) {
      console.warn('Duplicate event detected (409), trying to find existing event...');
      
      try {
        const existingEvent = await findExistingEvent(eventData, engine);
        if (existingEvent) {
          console.log('Found existing event, returning its ID:', existingEvent.event);
          return { 
            status: 'success', 
            message: 'Used existing event',
            eventId: existingEvent.event 
          };
        }
      } catch (findError) {
        console.warn('Could not find existing event:', findError);
      }
      
      return { 
        status: 'success', 
        message: 'Duplicate ignored (could not find existing event)',
        imported: 0,
        updated: 0,
        ignored: 1
      };
    }
    
    console.error('Non-409 error in saveKraOrKpi:', error);
    throw error;
  }
}

async function findExistingEvent(eventData, engine) {
  try {
    const objectiveLinkValue = eventData.dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
    if (!objectiveLinkValue) {
      console.warn('No objective link found in event data');
      return null;
    }
    
    // Also filter by specific data elements to find the exact type of event
    const eventTypeFilter = eventData.dataValues.some(dv => dv.dataElement === 'obapz6Gnyla')
      ? 'obapz6Gnyla' // KRA
      : eventData.dataValues.some(dv => dv.dataElement === 'DBmylDQA9KX')
      ? 'DBmylDQA9KX' // KPI
      : null;
    
    const filters = [`plJovUlxfQ0:eq:${objectiveLinkValue}`];
    if (eventTypeFilter) {
      filters.push(`${eventTypeFilter}:like:${eventData.dataValues.find(dv => dv.dataElement === eventTypeFilter)?.value}`);
    }
    
    const response = await engine.query({
      resource: 'events',
      params: {
        program: eventData.program,
        programStage: eventData.programStage,
        filter: filters,
        fields: 'event,dataValues[dataElement,value]',
        pageSize: 1
      }
    });
    
    return response.events[0] || null;
  } catch (error) {
    console.error('Error finding existing event:', error);
    return null;
  }
}

// Helper function to create event data for different types in Objectives program stage - Updated to use position TEI
export const createEventPayload = (type, data, objectiveId, orgUnitId, positionTeiId, enrollmentId) => {
  const baseEvent = {
    program: 'yiT00XKrrHP',
    programStage: 'VIBtpvkpHFR', // Objectives program stage
    orgUnit: orgUnitId,
    trackedEntityInstance: positionTeiId, // Use position TEI instead of staff TEI
    enrollment: enrollmentId,
    eventDate: new Date().toISOString().split('T')[0],
    status: 'ACTIVE',
    dataValues: []
  };

  const dataElementMappings = {
    kra: {
      name: 'obapz6Gnyla',
      description: 'DEECWPTj42E',
      weight: 'Xl4VEFoIFIw'
    },
    kpi: {
      name: 'DBmylDQA9KX',
      description: 'rQqF75V9t7K',
      target: 'Ru68Ax2Xo2L'
    }
  };

  const mapping = dataElementMappings[type];
  if (mapping) {
    Object.entries(data).forEach(([key, value]) => {
      if (mapping[key] && value !== null && value !== undefined) {
        baseEvent.dataValues.push({
          dataElement: mapping[key],
          value: String(value)
        });
      }
    });
  }

  // Link to objective
  if (objectiveId) {
    baseEvent.dataValues.push({
      dataElement: 'plJovUlxfQ0', // companyAlignedGoal
      value: objectiveId
    });
  }

  return baseEvent;
};
